import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  getRetailerWarehouses,
  createRetailerWarehouse,
  updateRetailerWarehouse,
  deleteRetailerWarehouse,
  resetRetailerWarehousesState,
} from 'store/retailer/warehouses';
import { RootState } from 'store';
import { useAppSelector, useAppDispatch } from 'hooks';
import { RetailerWarehouseBody } from 'resources/types';
import { queries, ROWS_PER_PAGES } from 'resources/constants';

const [defaultSize] = ROWS_PER_PAGES;

function useRetailerWarehouses() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const companyState = useAppSelector((state: RootState) => state.retailer.company);
  const { data: companyData } = companyState;

  const page: number = Number(searchParams.get(queries.PAGE)) || 0;
  const size: number = Number(searchParams.get(queries.SIZE)) || defaultSize;
  const search: string | null = searchParams.get(queries.SEARCH) || null;

  useEffect(() => () => {
    dispatch(resetRetailerWarehousesState());
  }, [dispatch]);

  useEffect(
    () => {
      if (companyData) {
        dispatch(getRetailerWarehouses({
          companyId: companyData.id,
          query: {
            page,
            size,
            ...(search ? { search: encodeURIComponent(search) } : {}),
          },
        }));
      }
    },
    [companyData, page, size, search, dispatch],
  );
}

function useRetailerWarehousesCallBacks() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const companyState = useAppSelector((state: RootState) => state.retailer.company);
  const { data: companyData } = companyState;

  const page: number = Number(searchParams.get(queries.PAGE)) || 0;
  const size: number = Number(searchParams.get(queries.SIZE)) || defaultSize;
  const search: string | null = searchParams.get(queries.SEARCH) || null;

  const deleteWarehouse = useCallback((warehouseId: number) => {
    if (companyData) {
      return dispatch(deleteRetailerWarehouse({ companyId: companyData.id, warehouseId }));
    }
    return null;
  }, [companyData, dispatch]);

  const createWarehouse = useCallback(
    (params: { data: RetailerWarehouseBody }) => {
      const { data } = params;
      if (companyData) {
        return dispatch(createRetailerWarehouse({ companyId: companyData.id, data }));
      }
      return null;
    },
    [companyData, dispatch],
  );

  const updateWarehouse = useCallback(
    (params: { warehouseId: number, data: RetailerWarehouseBody }) => {
      const { warehouseId, data } = params;
      if (companyData) {
        return dispatch(updateRetailerWarehouse({ companyId: companyData.id, warehouseId, data }));
      }
      return null;
    },
    [companyData, dispatch],
  );

  const getWarehouses = useCallback(
    () => {
      if (companyData) {
        return dispatch(getRetailerWarehouses({
          companyId: companyData.id,
          query: {
            page,
            size,
            ...(search ? { search: encodeURIComponent(search) } : {}),
          },
        }));
      }
      return null;
    },
    [companyData, dispatch, page, size, search],
  );

  return {
    getWarehouses, createWarehouse, updateWarehouse, deleteWarehouse,
  };
}

export { useRetailerWarehouses, useRetailerWarehousesCallBacks };
